import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { cacheClear } from "@/lib/swr";

const AuthContext = createContext(null);

// iter252/L5 — public visitors have no staff session, so calling /auth/me on the marketing
// site, the resident portal and the public signing/opt-in pages guaranteed a 401 and two
// console errors on every single page load. Only ask where a session could plausibly exist.
// Field Services adds four more no-login-ever surfaces: vendors and crew never get accounts.
const NEVER_STAFF_PREFIXES = ["/portal", "/c/", "/opt-in", "/sign", "/status", "/webdemo", "/demo",
                              "/vendor/", "/crew/", "/job/", "/visit/"];

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  // Every sign-in that lands (magic link, Google) bumps the generation. A /auth/me probe that
  // was fired BEFORE that sign-in — e.g. the one this provider sends on first paint — must not
  // be allowed to overwrite a fresh session with its stale "not signed in". That race sent
  // people who had just redeemed a valid link straight back to the login page (iter345).
  const gen = useRef(0);

  const setUser = useCallback((u) => {
    gen.current += 1;
    setUserState(u);
    setLoading(false);
  }, []);

  const checkAuth = useCallback(async () => {
    const mine = ++gen.current;
    try {
      const res = await api.get("/auth/me");
      if (mine === gen.current) setUserState(res.data);
    } catch {
      if (mine === gen.current) setUserState(null);
    } finally {
      if (mine === gen.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, let AuthCallback establish session first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const path = window.location.pathname;
    if (NEVER_STAFF_PREFIXES.some((p) => path === p || path.startsWith(p))) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    cacheClear(); // drop cached tab data so the next sign-in never sees another account's data
    setUser(null);
    try { localStorage.removeItem("rl_ml_pending"); } catch { /* ignore */ }
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
